// https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button

import {
  ButtonRoundedIconProps,
  StyledButtonProps,
  ThemeStyleRules,
} from './types'
import { Add } from 'components/Icons'
import styled, { css } from 'styled-components/macro'

const themes = {
  dark: {
    background: 'var(--surface-sunken)',
    boxShadow: 'var(--white)',
    color: 'var(--white)',
  },
  light: {
    background: 'var(--surface-card)',
    boxShadow: 'var(--action-primary)',
    color: 'var(--action-primary)',
  },
  white: {
    background: 'var(--surface-sunken)',
    boxShadow: 'var(--action-primary)',
    color: 'var(--action-primary)',
  },
}

const themeStyleRules = ({ backgroundColor }: ThemeStyleRules) => {
  const { background, boxShadow, color } = themes[backgroundColor]
  return css`
    box-shadow: inset 0 0 0 1px ${boxShadow};
    color: ${color};

    :hover:not(:active) {
      background: ${background};
    }
  `
}

const Button = styled.button<StyledButtonProps>`
  ${themeStyleRules};

  background: transparent;
  border: none;
  border-radius: var(--radius-pill);
  cursor: pointer;

  height: 3rem;
  width: 3.625rem;
  padding: 0;
  outline: none;

  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  justify-content: center;

  transition: box-shadow 50ms linear;
  user-select: none;

  svg {
    fill: currentcolor;
  }

  /* stylelint-disable-next-line no-descending-specificity  */
  :disabled {
    background: ${({ backgroundColor }) =>
      backgroundColor === 'dark' ? 'transparent' : 'var(--gray-100)'};
    box-shadow: inset 0 0 0 1px currentcolor;
    color: var(--gray-400);
    pointer-events: none;
  }
  /* stylelint-disable-next-line no-descending-specificity  */
  :active {
    background: var(--action-primary);
    box-shadow: inset 0 0 3px var(--action-primary);
    color: var(--action-on-primary);
  }

  :hover:not(:active) {
    color: var(--action-primary-hover);
  }

  :focus-visible:not(:active):not(:hover) {
    box-shadow: inset 0 0 0 2px var(--focus-ring);
  }
`
/**
 * the icon defaults to Add (+) if no child is provided

 */
export const ButtonRoundedIcon = ({
  backgroundColor = 'light',
  children,
  disabled = false,
  type = 'button',
  ...rest
}: ButtonRoundedIconProps) => (
  <Button
    data-testid="button-rounded-icon"
    backgroundColor={backgroundColor}
    disabled={disabled}
    type={type}
    {...rest}
  >
    {children ? children : <Add />}
  </Button>
)
